import { STANDARD_BRANCH } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { useLocalStorage } from '@festina-life/ui/hooks'
import { Activity, useEffect, useMemo } from 'react'
import {
  Group,
  Panel,
  Separator,
  useDefaultLayout,
} from 'react-resizable-panels'

import { useAvailableServersForConfiguration } from '../../system/useAvailableServersForConfiguration'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useAllConciseBranches } from '../../uiModels/branches/useAllConciseBranches'
import { useIsPersonaEditor } from '../../uiModels/useIsPersonaEditor'
import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import { BranchesPanelDetailsSelector } from './BranchesPanelDetailsSelector'
import { BranchesTree } from './branchesTree/BranchesTree'
import {
  isBranchTreeElementBranchKey,
  isBranchTreeElementConfigurableServer,
  isBranchTreeElementWorkspaceKey,
} from './branchesTree/helpers'
import { ACTIVE_BRANCH_ELEMENT_KEY } from './constants'
import type { BranchTreeElementKey } from './types'

export const BranchesPanel = () => {
  const { branch, server } = useNavigationParams()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const currentBranchKey = branch ? { name: branch, server } : undefined
  const initialActiveElementKey = currentWorkspaceKey ?? currentBranchKey
  const [activeElementKey, setActiveElementKey] = useLocalStorage<
    Optional<BranchTreeElementKey>
  >(ACTIVE_BRANCH_ELEMENT_KEY, initialActiveElementKey)
  const allConciseBranches = useAllConciseBranches()
  const allServers = useAvailableServersForConfiguration()
  const { conciseWorkspaces } = useWorkspaceManagerContext()
  const isPersonaEditor = useIsPersonaEditor()
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    groupId: 'branchesPanel',
    storage: localStorage,
  })
  const doesActiveElementExist = useMemo(() => {
    if (isBranchTreeElementBranchKey(activeElementKey)) {
      return (
        activeElementKey.name === STANDARD_BRANCH ||
        allConciseBranches[activeElementKey.server]?.some(
          (conciseBranch) => conciseBranch.name === activeElementKey.name,
        )
      )
    } else if (
      isBranchTreeElementWorkspaceKey(activeElementKey) &&
      isPersonaEditor
    ) {
      return conciseWorkspaces[activeElementKey.branchKey.server]?.some(
        (conciseWorkspace) => conciseWorkspace.name === activeElementKey.name,
      )
    } else if (
      isBranchTreeElementConfigurableServer(activeElementKey) &&
      isPersonaEditor
    ) {
      return allServers.includes(activeElementKey)
    }

    return false
  }, [
    activeElementKey,
    allConciseBranches,
    allServers,
    conciseWorkspaces,
    isPersonaEditor,
  ])

  useEffect(() => {
    if (!doesActiveElementExist && activeElementKey) {
      setActiveElementKey(undefined)
    }
  }, [activeElementKey, doesActiveElementExist, setActiveElementKey])

  return (
    <Group
      id="branchesPanel"
      orientation="vertical"
      defaultLayout={defaultLayout}
      onLayoutChanged={onLayoutChanged}
    >
      <Panel id="branchTreePanel" minSize="15%">
        <BranchesTree
          onSelect={setActiveElementKey}
          activeElementKey={activeElementKey}
        />
      </Panel>
      <Activity
        mode={activeElementKey && doesActiveElementExist ? 'visible' : 'hidden'}
      >
        <Separator
          id="resizeBranchPanelPicker"
          className="h-1.5 dark:bg-slate-700 bg-slate-300"
        />
        <Panel id="branchDetailsPanel" minSize="30%">
          {activeElementKey && (
            <BranchesPanelDetailsSelector
              // Force remount when activeElementKey changes to reset internal state of the details panel
              key={`details-${JSON.stringify(activeElementKey)}`}
              activeElementKey={activeElementKey}
              onClose={() => setActiveElementKey(undefined)}
            />
          )}
        </Panel>
      </Activity>
    </Group>
  )
}
